(() => {
  const officialOrange = '#FA8517';
  const welcomeFormId = 'app-embed-container-200037';
  let formsObserver = null;
  let observedFormsRoot = null;
  let documentObserver = null;
  let documentObserverTimer = 0;
  let lateScanFrame = 0;

  // The customer form confirms its POST in Liquid. Forms uses its native success redirect.
  // Both go through Shopify's discount link, which preserves compatible cart discounts.
  const connectWelcomeSignup = () => {
    const section = document.querySelector('#memys-newsletter');
    if (!section || section.dataset.memysWelcomeHandled === 'true') return;
    const success = section.querySelector('[data-memys-registration-confirmed]');
    const inputs = section.querySelector('[data-memys-newsletter-inputs]');
    if (!success || !inputs) return;
    const url = new URL(window.location.href);
    const welcomeCallback = url.searchParams.get('memys_bienvenida');
    const returnedFromDiscount = welcomeCallback === '1' || welcomeCallback === '1#memys-newsletter';
    const registrationConfirmed = success.dataset.memysRegistrationConfirmed === 'true';
    if (!returnedFromDiscount && !registrationConfirmed) return;
    section.dataset.memysWelcomeHandled = 'true';

    if (returnedFromDiscount) {
      success.hidden = false;
      inputs.hidden = true;
      url.searchParams.delete('memys_bienvenida');
      url.searchParams.delete('customer_posted');
      url.hash = 'memys-newsletter';
      // Consume the callback so reload/back cannot apply a code the customer removed.
      window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash);
      success.focus({ preventScroll: true });
      section.scrollIntoView({ block: 'start', behavior: 'instant' });
      if (document.readyState !== 'complete') {
        window.addEventListener('load', () => section.scrollIntoView({ block: 'start', behavior: 'instant' }), { once: true });
      }
      return;
    }

    // Do not mark a code as applied or merely prefill a field: visit the official endpoint.
    const discountUrl = new URL(section.dataset.memysDiscountUrl, window.location.origin);
    if (discountUrl.origin !== window.location.origin || discountUrl.pathname !== '/discount/MEMYSCLIENTE') return;
    window.location.replace(discountUrl.href);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', connectWelcomeSignup, { once: true });
  } else {
    connectWelcomeSignup();
  }

  const directChildInside = (element, ancestor) => {
    let current = element;
    while (current?.parentElement && current.parentElement !== ancestor) current = current.parentElement;
    return current?.parentElement === ancestor ? current : element;
  };

  const hideShopCapture = (root) => {
    const form = root.querySelector('[data-testid="form"]');
    const normalize = (value) => String(value || '').replace(/\s+/g, ' ').trim().toLocaleLowerCase('es');
    const candidates = [...root.querySelectorAll('button, a, [role="button"], span, p, div')];
    const shopControl = candidates.find((element) => {
      const text = normalize(element.textContent);
      const label = normalize(element.getAttribute('aria-label'));
      return text === 'reclamar con shop' || text === 'claim with shop' || label === 'reclamar con shop';
    });

    if (shopControl) {
      const interactive = shopControl.closest('button, a, [role="button"]') || shopControl;
      const container = form?.contains(interactive) ? directChildInside(interactive, form) : interactive;
      container.style.setProperty('display', 'none', 'important');
      container.setAttribute('aria-hidden', 'true');
    }

    candidates
      .filter((element) => ['o', 'or'].includes(normalize(element.textContent)))
      .forEach((separator) => {
        const container = form?.contains(separator) ? directChildInside(separator, form) : separator;
        container.style.setProperty('display', 'none', 'important');
        container.setAttribute('aria-hidden', 'true');
      });
  };

  const polishForms = () => {
    const root = document.getElementById(welcomeFormId)?.shadowRoot;
    if (!root) return false;

    let styles = root.querySelector('#memys-forms-professional');
    if (!styles) {
      styles = document.createElement('style');
      styles.id = 'memys-forms-professional';
      styles.textContent = `
        :host {
          --button-background-color: ${officialOrange} !important;
          --button-text-color: #111111 !important;
          --forms-accent-color: ${officialOrange} !important;
        }

        [role="dialog"] > [data-sizing="form-wrapper"] {
          border-radius: 18px !important;
          overflow: hidden !important;
        }

        [role="dialog"] {
          background-color: transparent !important;
        }

        [role="dialog"] > [data-sizing="form-wrapper"] {
          background-color: #ffffff !important;
        }

        [id^="label-"] {
          color: #595959 !important;
        }

        [role="button"][aria-label="Close modal"] {
          display: grid !important;
          width: 44px !important;
          height: 44px !important;
          place-items: center !important;
        }

        @media (max-width: 749px) {
          [data-form-root][data-current-step="tease"] {
            bottom: max(72px, calc(60px + env(safe-area-inset-bottom))) !important;
          }
        }

        @media (max-width: 989px) {
          [role="dialog"]:has(> [class*="overlayBackground"]) {
            box-sizing: border-box !important;
            align-items: center !important;
            justify-content: center !important;
            padding: max(12px, env(safe-area-inset-top)) max(12px, env(safe-area-inset-right)) max(12px, env(safe-area-inset-bottom)) max(12px, env(safe-area-inset-left)) !important;
          }

          [role="dialog"] > [data-sizing="form-wrapper"] {
            grid-template-columns: minmax(0, 1fr) !important;
            grid-template-rows: 110px minmax(0, auto) !important;
            grid-template-areas: "image" "content" !important;
            width: min(460px, 100%) !important;
            min-width: 0 !important;
            max-width: 100% !important;
            height: auto !important;
            min-height: 0 !important;
            max-height: calc(100dvh - 24px - env(safe-area-inset-top) - env(safe-area-inset-bottom)) !important;
            margin: 0 auto !important;
            overflow: hidden !important;
            border-radius: 18px !important;
            box-shadow: 0 20px 60px rgba(17, 17, 17, 0.28) !important;
          }

          [role="dialog"] > [data-sizing="form-wrapper"],
          [role="dialog"] > [data-sizing="form-wrapper"] * {
            box-sizing: border-box !important;
          }

          [role="dialog"] > [data-sizing="form-wrapper"] > div[style*="background-image"] {
            display: block !important;
            grid-area: image !important;
            width: 100% !important;
            height: 110px !important;
            min-height: 110px !important;
            background-position: center 44% !important;
            background-size: cover !important;
          }

          [role="dialog"] > [data-sizing="form-wrapper"] > div:not([style*="background-image"]) {
            grid-area: content !important;
            align-self: auto !important;
            height: auto !important;
            min-height: 0 !important;
            max-height: calc(100dvh - 158px - env(safe-area-inset-top) - env(safe-area-inset-bottom)) !important;
            padding: 18px 16px max(18px, env(safe-area-inset-bottom)) !important;
            overflow-y: auto !important;
            overflow-x: hidden !important;
            overscroll-behavior: contain !important;
            -webkit-overflow-scrolling: touch !important;
          }

          [data-sizing="form-wrapper"] input,
          [data-sizing="form-wrapper"] button,
          [data-sizing="form-wrapper"] textarea,
          [data-sizing="form-wrapper"] select {
            max-width: 100% !important;
          }

          .formContainerCloseButtonPosition {
            top: 8px !important;
            right: 8px !important;
            z-index: 3 !important;
          }

          [class*="formContainerCloseButtonPosition"] {
            top: 8px !important;
            right: 8px !important;
            width: 48px !important;
            height: 48px !important;
            padding: 2px !important;
          }

          [data-testid="form"] {
            gap: 12px !important;
          }

          [data-sizing="form-wrapper"] h1,
          [data-sizing="form-wrapper"] h2,
          [data-sizing="form-wrapper"] h3 {
            max-width: 100% !important;
            padding-right: 42px !important;
            box-sizing: border-box !important;
          }

          [data-sizing="form-wrapper"] p {
            white-space: normal !important;
            overflow-wrap: anywhere !important;
            text-overflow: clip !important;
          }
        }

        @media (max-width: 989px) and (max-height: 620px),
          (max-width: 989px) and (orientation: landscape) {
          [role="dialog"] > [data-sizing="form-wrapper"] {
            grid-template-rows: 56px minmax(0, auto) !important;
          }

          [role="dialog"] > [data-sizing="form-wrapper"] > div[style*="background-image"] {
            height: 56px !important;
            min-height: 56px !important;
          }

          [role="dialog"] > [data-sizing="form-wrapper"] > div:not([style*="background-image"]) {
            max-height: calc(100dvh - 104px - env(safe-area-inset-top) - env(safe-area-inset-bottom)) !important;
            padding: 10px 14px max(10px, env(safe-area-inset-bottom)) !important;
          }

          [data-sizing="form-wrapper"] h1,
          [data-sizing="form-wrapper"] h2,
          [data-sizing="form-wrapper"] h3 {
            margin-block: 0 6px !important;
            font-size: clamp(22px, 5.5vw, 26px) !important;
            line-height: 1.08 !important;
          }

          [data-sizing="form-wrapper"] p {
            margin-block: 4px 8px !important;
            font-size: 13px !important;
            line-height: 1.25 !important;
          }

          [data-testid="form"] {
            gap: 8px !important;
          }
        }

      `;
      root.append(styles);
    }

    const dialog = root.querySelector('[role="dialog"]');
    if (dialog && !dialog.hasAttribute('aria-label') && !dialog.hasAttribute('aria-labelledby')) {
      dialog.setAttribute('aria-label', 'Envío gratis en tu primera compra');
    }

    const heading = root.querySelector('h1, h2, h3');
    if (heading?.textContent?.includes('Envio Gratis')) {
      heading.textContent = heading.textContent.replace('Envio Gratis', 'Envío gratis');
    }

    const disclaimer = [...root.querySelectorAll('p')].find((item) => /registrarte.*notificaciones/i.test(item.textContent || ''));
    if (disclaimer) {
      disclaimer.textContent =
        'Al suscribirte, aceptas recibir promociones y descuentos por email. Puedes darte de baja cuando quieras.';
    }

    hideShopCapture(root);

    return true;
  };

  const monitorForms = () => {
    const root = document.getElementById(welcomeFormId)?.shadowRoot;
    if (!root) return false;

    if (root !== observedFormsRoot) {
      formsObserver?.disconnect();
      observedFormsRoot = root;
      formsObserver = new MutationObserver(() => {
        polishForms();
      });
      formsObserver.observe(root, { childList: true, subtree: true });
    }

    polishForms();
    return true;
  };

  const formatLoyalty = () => {
    const line = document.querySelector('.honeypop-pp-text > p:first-child');
    if (!line || line.dataset.memysFormatted === 'true') return false;

    const match = (line.textContent || '').match(/Gana\s+([\d.,]+)\s+puntos.*?~\s*([\d.,]+)\s*\$/i);
    if (!match) return false;

    const points = Number(match[1].replace(/\D/g, ''));
    const value = Number(match[2].replace(/\D/g, ''));
    if (!Number.isFinite(points) || !Number.isFinite(value)) return false;

    line.textContent = `${new Intl.NumberFormat('es-CL').format(points)} puntos · valor estimado $${new Intl.NumberFormat(
      'es-CL'
    ).format(value)}`;
    line.dataset.memysFormatted = 'true';
    return true;
  };

  const fixFrameNames = () => {
    const frames = [
      [document.querySelector('#merchantwidgetiframe'), 'Opiniones de Google sobre MemysHuevo'],
      [document.querySelector('#PBarNextFrame'), 'Barra privada de vista previa de Shopify'],
    ];

    frames.forEach(([frame, title]) => {
      if (frame && !frame.hasAttribute('title')) frame.setAttribute('title', title);
    });
  };

  const installNewsletterAnchor = () => {
    const section = document.querySelector('[id$="__section_Uh4FyJ"]');
    if (!section) return false;

    let anchor = document.querySelector('#memys-newsletter');
    if (!anchor) {
      anchor = document.createElement('span');
      anchor.id = 'memys-newsletter';
      anchor.setAttribute('aria-hidden', 'true');
      Object.assign(anchor.style, {
        display: 'block',
        position: 'relative',
        top: '-96px',
        width: '1px',
        height: '1px',
        overflow: 'hidden',
      });
      section.prepend(anchor);
    }

    if (window.location.hash === '#memys-newsletter') {
      window.requestAnimationFrame(() => section.scrollIntoView({ block: 'start' }));
    }

    return true;
  };

  const focusableSelector = 'a[href], button, input, select, textarea, [tabindex]';
  const syncSlideFocus = (slide) => {
    const isHidden = slide.getAttribute('aria-hidden') === 'true';

    slide.querySelectorAll(focusableSelector).forEach((element) => {
      if (isHidden) {
        if (!element.hasAttribute('data-memys-original-tabindex')) {
          element.dataset.memysOriginalTabindex = element.hasAttribute('tabindex') ? element.getAttribute('tabindex') : 'none';
        }
        element.setAttribute('tabindex', '-1');
        return;
      }

      if (!element.hasAttribute('data-memys-original-tabindex')) return;
      const original = element.dataset.memysOriginalTabindex;
      if (original === 'none') element.removeAttribute('tabindex');
      else element.setAttribute('tabindex', original);
      delete element.dataset.memysOriginalTabindex;
    });
  };

  const slideObserver = new MutationObserver((records) => {
    records.forEach((record) => syncSlideFocus(record.target));
  });

  const fixCarouselFocus = () => {
    document.querySelectorAll('slideshow-slide[aria-hidden]').forEach((slide) => {
      if (slide.dataset.memysFocusObserved !== 'true') {
        slide.dataset.memysFocusObserved = 'true';
        slideObserver.observe(slide, { attributes: true, attributeFilter: ['aria-hidden'] });
      }
      syncSlideFocus(slide);
    });
  };

  const fixProductSemantics = () => {
    const currentHeading = document.querySelector('h3.bulk-discount-title');
    if (currentHeading) {
      const replacement = document.createElement('h2');
      [...currentHeading.attributes].forEach((attribute) => replacement.setAttribute(attribute.name, attribute.value));
      replacement.innerHTML = currentHeading.innerHTML;
      currentHeading.replaceWith(replacement);
    }

    if (document.querySelector('main[data-template^="collection"]')) {
      document.querySelectorAll('product-card [role="heading"][aria-level="3"]').forEach((heading) => {
        heading.setAttribute('aria-level', '2');
      });
    }
  };

  const enhanceBulkOptions = () => {
    const options = [...document.querySelectorAll('.bulk-option-card[data-qty]')];
    if (!options.length) return false;

    const syncPressedState = () => {
      options.forEach((option) => option.setAttribute('aria-pressed', String(option.classList.contains('active'))));
    };

    options.forEach((option) => {
      option.setAttribute('role', 'button');
      option.setAttribute('tabindex', '0');
      // The visible offer copy is already a complete accessible name. Keeping
      // a shorter aria-label caused a mismatch for voice-control users.
      option.removeAttribute('aria-label');

      if (option.dataset.memysAccessible === 'true') return;
      option.dataset.memysAccessible = 'true';
      option.addEventListener('click', () => window.requestAnimationFrame(syncPressedState));
      option.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        option.click();
      });
    });

    syncPressedState();
    return true;
  };

  const scanLateComponents = () => {
    const formsReady = monitorForms();
    const loyaltyRequired = Boolean(document.querySelector('main[data-template^="product"]'));
    const loyaltyReady = !loyaltyRequired || formatLoyalty();
    fixFrameNames();

    if (formsReady && loyaltyReady && document.querySelector('#merchantwidgetiframe')) {
      documentObserver?.disconnect();
      documentObserver = null;
      if (documentObserverTimer) window.clearTimeout(documentObserverTimer);
      documentObserverTimer = 0;
    }
  };

  const scheduleLateScan = () => {
    if (lateScanFrame) return;
    lateScanFrame = window.requestAnimationFrame(() => {
      lateScanFrame = 0;
      scanLateComponents();
    });
  };

  const startLateComponentObserver = () => {
    scanLateComponents();
    if (documentObserver || !document.body) return;

    documentObserver = new MutationObserver(scheduleLateScan);
    documentObserver.observe(document.body, { childList: true, subtree: true });
    documentObserverTimer = window.setTimeout(() => {
      scanLateComponents();
      documentObserver?.disconnect();
      documentObserver = null;
      documentObserverTimer = 0;
    }, 12000);
  };

  const run = () => {
    installNewsletterAnchor();
    fixFrameNames();
    fixCarouselFocus();
    fixProductSemantics();
    enhanceBulkOptions();
  };

  const schedule = window.requestIdleCallback
    ? (callback) => window.requestIdleCallback(callback, { timeout: 1800 })
    : (callback) => window.setTimeout(callback, 900);

  schedule(run);
  schedule(startLateComponentObserver);
  window.addEventListener('load', scheduleLateScan, { once: true });
  ['pointerdown', 'keydown', 'touchstart'].forEach((eventName) => {
    window.addEventListener(eventName, scheduleLateScan, { once: true, passive: true });
  });
})();

